import { FC, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { useInView } from 'react-intersection-observer';

import { Close, Share, SolidStar } from '@agendapro/emerald-icons';
import { useInfiniteQuery } from 'react-query';
import { useMediaQuery } from 'react-responsive';
import Image from 'next/image';
import { getRandomNumber, getTailwindWidth } from '@/utils';
import { Skeleton, devices } from '@/UI';
import { useShowComponent } from '@/hooks/showComponent';
import useLockBodyScroll from '@/hooks/lockBodyScroll';

import { useCommentReview, useGetReviewSummary } from '@/services/bookings';
import { getReviews } from '@/services/bookings/bookings.repo';
import { useDialogsContext } from '@/context/DialogsContext';
import { useQueryParamDialog } from '@/hooks';

const ReviewSkeleton: FC = () => (
  <div className="py-2 border-solid border-b border-b-gray20 pb-2">
    <div className="flex justify-between pb-1">
      <div className="w-96">
        <Skeleton className={`h-4 rounded-sm ${getTailwindWidth(getRandomNumber(3, 12))}`} />
      </div>
      <div className="flex flex-col items-end pb-2">
        <div className="w-24 flex justify-end">
          <Skeleton className={`${getTailwindWidth(getRandomNumber(6, 12))} h-4 bg-edit20 rounded-sm`} />
        </div>
        <Skeleton className="h-3 w-16 mt-1 bg-background rounded-sm" />
      </div>
    </div>
    <div className="flex flex-col pb-2 w-full">
      {[...Array(getRandomNumber(2, 7)).keys()].map((i) => (
        <Skeleton className={`h-3 mb-2 ${getTailwindWidth(getRandomNumber(5, 12))} rounded-sm`} key={i} />
      ))}
    </div>
  </div>
);
const Review: FC<{
  comment: string;
  date: string;
  name: string | null;
  rating: number;
  serviceName: string;
  canReply?: boolean;
  uuid: string;
  comments?: { comment: string; id: number }[];
  image?: string;
  onCommentReviewSuccess?: () => void;
}> = ({ comment, date, name, rating, serviceName, canReply, uuid, comments, onCommentReviewSuccess, image }) => {
  const { t } = useTranslation();
  const { setErrorDialog } = useDialogsContext();

  const [isReplying, setIsReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const { mutate: commentReview } = useCommentReview({
    onSuccess: () => {
      setIsReplying(false);
      setReplyText('');
      onCommentReviewSuccess?.();
    },
    onError: (error) => {
      setIsReplying(false);
      setErrorDialog({
        open: true,
        errorDetail: error?.response?.data?.error || error?.message || t('ERRORS.GENERIC'),
      });
    },
  });

  // get last comment (the most recent)
  const lastComment = comments && comments.length > 0 && comments[comments.length - 1];
  const hasComment = !!lastComment;

  const handleStartEdit = () => {
    setIsReplying(true);
    if (lastComment) {
      setReplyText(lastComment.comment);
    }
  };

  const handleCancel = () => {
    setIsReplying(false);
    setReplyText('');
  };

  const getTimeSince = (date) => {
    const days = dayjs().diff(date, 'day');
    const months = dayjs().diff(date, 'month');
    const years = dayjs().diff(date, 'year');

    if (years >= 1) {
      if (years === 1) {
        return t('REVIEW.YEAR_SINCE_REVIEW');
      }
      return t('REVIEW.YEARS_SINCE_REVIEW', { amount: years });
    }

    if (months >= 1) {
      if (months === 1) {
        return t('REVIEW.MONTH_SINCE_REVIEW');
      }
      return t('REVIEW.MONTHS_SINCE_REVIEW', { amount: months });
    }

    if (days === 1) {
      return t('REVIEW.DAY_SINCE_REVIEW');
    }

    if (days === 0) {
      return t('REVIEW.TODAY');
    }

    return t('REVIEW.DAYS_SINCE_REVIEW', { amount: days });
  };

  return (
    <div className="py-2 border-solid border-b border-b-gray20">
      <div className="flex justify-between pb-1">
        <p className="text-sm font-bold text-gray">{serviceName}</p>
        <div className="flex justify-center items-center">
          {[...Array(5).keys()].map((star, i) => (
            <SolidStar
              size={17}
              key={star}
              className={`${i < rating ? '[&>path]:fill-edit' : '[&>path]:fill-gray20'} mr-1`}
            />
          ))}
        </div>
      </div>
      <div className="flex justify-between pb-2 w-full">
        {name && <p className="text-xs text-gray50">{`${t('REVIEW.BY')} ${name}`}</p>}
        <p className="text-xs text-gray50 mr-0 ml-auto">{getTimeSince(date)}</p>
      </div>
      {comment && <p className="text-sm text-gray80 whitespace-pre-wrap break-words">{`"${comment}"`}</p>}
      {image && (
        <div className="inline-block h-40 my-2 leading-none text-[0] rounded-md overflow-hidden [&>span]:inline-block [&>span]:!h-40 [&>span]:!w-auto [&>span]:leading-none [&>span]:text-[0] [&>span]:rounded-md [&>span]:overflow-hidden [&>span]:align-top [&>span>img]:!h-40 [&>span>img]:!w-auto [&>span>img]:!max-w-none [&>span>img]:!min-w-0 [&>span>img]:object-contain [&>span>img]:rounded-md [&>span>img]:block [&>span>img]:m-0 [&>span>img]:p-0">
          <Image
            src={image}
            alt="Review"
            width={420}
            height={160}
            className="!h-40 !w-auto !max-w-none !min-w-0 object-contain !block rounded-md !m-0 !p-0"
            unoptimized
          />
        </div>
      )}
      {lastComment && (
        <div className="w-full flex flex-col items-end gap-1">
          <p className="text-xs text-gray50 text-right font-semibold">{t('REVIEW.REPLY_BY_LOCATION')}</p>
          <p className="text-sm text-gray50 whitespace-pre-wrap break-words text-right">{`"${lastComment.comment}"`}</p>
        </div>
      )}
      {canReply && !isReplying && (
        <button type="button" className="flex items-center gap-1 ml-auto" onClick={handleStartEdit}>
          {!hasComment && <Share size={16} className="[&>path]:fill-icon-brand" />}
          <p className="text-text-brand underline text-sm font-medium">
            {t(hasComment ? 'REVIEW.EDIT_REPLY' : 'REVIEW.REPLY')}
          </p>
        </button>
      )}
      {canReply && isReplying && (
        <div className="mt-2">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder={t('REVIEW.REPLY_PLACEHOLDER')}
            className="w-full p-2 border border-gray20 rounded-md resize-none focus:outline-none focus:border-border-brand"
            rows={3}
          />
          <div className="flex gap-2 mt-2 justify-end">
            <button
              type="button"
              className="flex h-fit bg-background-primary border border-border-brand border-solid rounded-md py-1 px-3 flew-row items-center justify-center gap-1 text-text-brand text-sm hover:bg-background-secondary transition-colors duration-300"
              onClick={handleCancel}
            >
              {t('REVIEW.CANCEL')}
            </button>
            <button
              type="button"
              disabled={!replyText}
              className="flex h-fit text-text-btnPrimary bg-background-btnPrimary border border-border-brand border-solid rounded-md py-1 px-3 flew-row items-center justify-center gap-1 text-sm hover:bg-background-btnPrimaryHover transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => {
                commentReview({ confirmationCode: uuid, comment: replyText });
              }}
            >
              {t(hasComment ? 'REVIEW.EDIT_REPLY' : 'REVIEW.SEND_REPLY')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ReviewsInfo: FC<{ showInXLarge?: boolean; canReply?: boolean; reviewsv2?: boolean }> = ({
  showInXLarge = true,
  canReply = false,
  reviewsv2 = false,
}) => {
  const reviewDialog = useShowComponent();
  const { t } = useTranslation();
  const isDesktop = useMediaQuery({ query: devices.XLarge });
  const router = useRouter();
  const { locationId } = router.query as { locationId: string };
  const { ref, inView } = useInView();
  const { data: reviewSummary, isLoading, isError } = useGetReviewSummary(locationId, reviewsv2);
  const isDetailed = reviewSummary?.reviewsOption === 'details';
  const {
    data: reviewsDetail,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isSuccess,
    refetch,
  } = useInfiniteQuery(
    ['projects', locationId],
    async ({ pageParam = 1 }) => getReviews(locationId, pageParam, 5, reviewsv2),
    {
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;

        if (reviewsv2) {
          return lastPage?.pagination?.nextPage ? nextPage : undefined;
        }

        return lastPage?.reviews?.length !== 0 ? nextPage : undefined;
      },
    },
  );

  useLockBodyScroll(reviewDialog.isShown);

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  useQueryParamDialog('reviews_open', reviewDialog, isDetailed && (reviewSummary?.totalReviews ?? 0) > 0);

  if (isLoading) {
    return <Skeleton className="w-24 rounded-sm h-4" />;
  }
  if (reviewSummary?.reviewsOption === 'disable' || reviewSummary?.totalReviews === 0 || isError) {
    return null;
  }
  return (
    <>
      <button
        type="button"
        className={`flex items-center xl:items-start md:gap-1 pt-1 ${showInXLarge ? 'flex' : 'xl:hidden'} ${
          isDetailed ? '' : 'cursor-auto'
        }`}
        onClick={isDetailed ? () => reviewDialog.show() : undefined}
      >
        {[...Array(5).keys()].map((star, i) => (
          <SolidStar
            size={isDesktop ? 20 : 16}
            key={star}
            className={`${i < reviewSummary?.average ? '[&>path]:fill-[#FFB057]' : '[&>path]:fill-[#B9B9C3]'} mr-1`}
          />
        ))}
        <p className="ml-1 text-[#1D1D3D]">
          <b>{reviewSummary?.average} </b>
          <span className="text-xs">
            (
            <span className={`text-[#4A4A64] ${isDetailed ? 'underline' : ''}`}>
              {isDetailed
                ? t('REVIEW.TOTAL_SEE_REVIEWS', { amount: reviewSummary?.totalReviews })
                : t('REVIEW.TOTAL_REVIEWS', { amount: reviewSummary?.totalReviews })}
            </span>
            )
          </span>
        </p>
      </button>
      {reviewDialog.isShown && (
        <div className="fixed top-0 left-0 w-full h-full bg-gray30 z-40 flex justify-center mt-0 items-center">
          <div className="bg-white rounded-sm xl:w-1/3 xl:h-2/3 w-full h-full pl-4 pr-3 pt-4 xl:pl-8 flex flex-col">
            <div className="flex justify-between pb-4 md:pr-5">
              <p className="text-lg font-bold">{t('REVIEW.MODAL_TITLE')}</p>
              <button type="button" onClick={() => reviewDialog.hide()}>
                <Close size={25} />
              </button>
            </div>
            <div className="overflow-y-auto h-full md:pr-5 no-scrollbar xl:show-scrollbar">
              <p className="text-base font-medium pb-3">{t('REVIEW.MODAL_DESCRIPTION')}</p>
              <div>
                {isSuccess &&
                  reviewsDetail.pages.flatMap((page) =>
                    page.reviews?.map((review) => (
                      <Review
                        key={review.id}
                        comment={review.comment}
                        rating={review.stars}
                        name={review.reviewerName}
                        date={review.reviewDate}
                        serviceName={review.serviceName}
                        canReply={canReply && reviewsv2}
                        uuid={review.uuid}
                        comments={review.reviewComments}
                        image={review.reviewImages?.[0]?.url || undefined}
                        onCommentReviewSuccess={() => refetch()}
                      />
                    )),
                  )}
                <div className="w-full h-10" ref={ref} />
                {(isFetching || isFetchingNextPage) &&
                  [...Array(5).keys()].map((item) => <ReviewSkeleton key={item} />)}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReviewsInfo;
